import React, { useEffect, useRef } from 'react';
import {
  Users, PenTool, HardHat, ClipboardList, Eye,
  Building2, Package, Wrench,
  Home, Store, Landmark,
  ArrowRight,
} from 'lucide-react';

const useScrollAnimation = (threshold = 0.08) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.querySelectorAll('.fade-up, .fade-left, .fade-right, .scale-in').forEach((child) => {
            child.classList.add('visible');
          });
          observer.unobserve(el);
        }
      },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);
  return ref;
};

// ── Data ──────────────────────────────────────────────────────────────────
const categories = [
  {
    icon: Users,
    title: 'Equipo multidisciplinario',
    description: 'Profesionales especializados en cada etapa del proyecto.',
    items: [
      { icon: PenTool,       label: 'Arquitectos',                  detail: 'Diseño y planificación' },
      { icon: HardHat,       label: 'Ingenieros civiles',           detail: 'Cálculo y estructura' },
      { icon: ClipboardList, label: 'Área de costos y presupuestos',detail: 'Control financiero de obra' },
      { icon: Eye,           label: 'Residentes de obra',           detail: 'Supervisión y seguimiento' },
    ],
  },
  {
    icon: Building2,
    title: 'Infraestructura propia',
    description: 'Recursos físicos que garantizan autonomía y rapidez.',
    items: [
      { icon: Building2, label: 'Oficina administrativa',       detail: 'Gestión, atención y coordinación' },
      { icon: Package,   label: 'Bodegas propias',            detail: '120 m² + 200 m² · Almacén certificado' },
      { icon: Wrench,    label: 'Maquinaria y herramientas',  detail: 'Equipo propio de construcción' },
      { icon: Users,     label: 'Personal técnico especializado', detail: 'Cuadrillas propias y subcontratistas' },
    ],
  },
  {
    icon: Landmark,
    title: 'Tipos de proyectos',
    description: 'Capacidad comprobada en tres grandes segmentos.',
    items: [
      { icon: Home,     label: 'Proyectos residenciales', detail: 'Casas y fraccionamientos' },
      { icon: Store,    label: 'Proyectos comerciales',   detail: 'Locales, sucursales y plazas' },
      { icon: Landmark, label: 'Obra pública',            detail: 'Contratos estatales y federales' },
      { icon: Building2,label: 'Obra de infraestructura', detail: 'Pavimentación y urbanización' },
    ],
  },
];

const bottomStats = [
  { value: '320 m²', label: 'En bodegas propias' },
  { value: '5+', label: 'Perfiles técnicos especializados' },
  { value: '200+', label: 'Proyectos ejecutados' },
  { value: '12+', label: 'Años de trayectoria' },
];

// ── Component ─────────────────────────────────────────────────────────────
const TechnicalCapacity = () => {
  const ref = useScrollAnimation();

  return (
    <section
      ref={ref}
      style={{
        padding: '8rem 2rem',
        background: 'var(--band-bg)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background texture */}
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: 'linear-gradient(var(--band-grid) 1px, transparent 1px), linear-gradient(90deg, var(--band-grid) 1px, transparent 1px)',
        backgroundSize: '60px 60px',
        pointerEvents: 'none',
      }} />
      {/* Red glow top-right */}
      <div style={{
        position: 'absolute', top: '-10%', right: '-5%',
        width: 500, height: 500, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(196,30,58,0.12) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />

      <div style={{ position: 'relative', zIndex: 1, maxWidth: 1200, margin: '0 auto' }}>

        {/* ── HEADER ─────────────────────────────────────────────────── */}
        <div className="fade-up" style={{ textAlign: 'center', marginBottom: '4.5rem' }}>
          {/* Overline pill */}
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
            background: 'rgba(196,30,58,0.15)',
            border: '1px solid rgba(196,30,58,0.3)',
            borderRadius: 20, padding: '0.35rem 1rem',
            marginBottom: '1.25rem',
          }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--red)' }} />
            <span style={{
              fontFamily: 'var(--font-heading)', fontWeight: 700,
              fontSize: '0.65rem', letterSpacing: '0.2em', textTransform: 'uppercase',
              color: 'var(--red)',
            }}>
              Capacidad técnica
            </span>
          </div>

          <h2 style={{
            fontFamily: 'var(--font-heading)', fontWeight: 900,
            fontSize: 'clamp(2rem, 4vw, 3rem)', color: 'var(--band-text)',
            lineHeight: 1.15, marginBottom: '1rem',
          }}>
            Listos para proyectos{' '}
            <span style={{ color: 'var(--red)' }}>de cualquier escala</span>
          </h2>
          <p style={{
            fontFamily: 'var(--font-body)', fontSize: '1rem',
            color: 'var(--band-text-55)', maxWidth: 540, margin: '0 auto', lineHeight: 1.75,
          }}>
            Contamos con el equipo humano, la infraestructura y la experiencia para ejecutar
            proyectos residenciales, comerciales y de obra pública con los más altos estándares.
          </p>
        </div>

        {/* ── 3 CATEGORY CARDS ───────────────────────────────────────── */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '1.5rem',
          marginBottom: '4rem',
        }} className="tc-grid">
          {categories.map((cat, i) => {
            const CatIcon = cat.icon;
            return (
              <div
                key={i}
                className={`fade-up delay-${i + 1}`}
                style={{
                  background: 'var(--band-surface)',
                  border: '1px solid var(--band-border)',
                  borderRadius: 20,
                  padding: '2.25rem',
                  transition: 'background 0.35s ease, border-color 0.35s ease, transform 0.35s ease',
                  cursor: 'default',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--band-surface-hover)';
                  e.currentTarget.style.borderColor = 'rgba(196,30,58,0.4)';
                  e.currentTarget.style.transform = 'translateY(-4px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'var(--band-surface)';
                  e.currentTarget.style.borderColor = 'var(--band-border)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                {/* Card header */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', marginBottom: '1.75rem' }}>
                  <div style={{
                    width: 52, height: 52, borderRadius: 14, flexShrink: 0,
                    background: 'var(--red)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 8px 24px rgba(196,30,58,0.35)',
                  }}>
                    <CatIcon size={24} color="white" strokeWidth={1.75} />
                  </div>
                  <div>
                    <h3 style={{
                      fontFamily: 'var(--font-heading)', fontWeight: 800,
                      fontSize: '1rem', color: 'var(--band-text)', lineHeight: 1.25,
                      marginBottom: '0.35rem',
                    }}>
                      {cat.title}
                    </h3>
                    <p style={{
                      fontFamily: 'var(--font-body)', fontSize: '0.78rem',
                      color: 'var(--band-text-45)', lineHeight: 1.5,
                    }}>
                      {cat.description}
                    </p>
                  </div>
                </div>

                {/* Divider */}
                <div style={{
                  height: 1, background: 'var(--band-divider)',
                  marginBottom: '1.5rem',
                }} />

                {/* Item list */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {cat.items.map((item, j) => {
                    const ItemIcon = item.icon;
                    return (
                      <div key={j} style={{ display: 'flex', alignItems: 'center', gap: '0.875rem' }}>
                        {/* Icon */}
                        <div style={{
                          width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                          background: 'rgba(196,30,58,0.12)',
                          border: '1px solid rgba(196,30,58,0.2)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <ItemIcon size={16} color="var(--red)" strokeWidth={1.75} />
                        </div>
                        {/* Text */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{
                            fontFamily: 'var(--font-heading)', fontWeight: 700,
                            fontSize: '0.875rem', color: 'var(--band-text)', lineHeight: 1.2,
                          }}>
                            {item.label}
                          </div>
                          <div style={{
                            fontFamily: 'var(--font-body)', fontSize: '0.72rem',
                            color: 'var(--band-text-40)', marginTop: 2,
                          }}>
                            {item.detail}
                          </div>
                        </div>
                        {/* Check dot */}
                        <div style={{
                          width: 8, height: 8, borderRadius: '50%',
                          background: 'var(--red)', flexShrink: 0,
                          boxShadow: '0 0 6px rgba(196,30,58,0.6)',
                        }} />
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* ── BOTTOM STATS BAR ───────────────────────────────────────── */}
        <div
          className="fade-up"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            borderRadius: 16,
            overflow: 'hidden',
            border: '1px solid var(--band-border)',
          }}
          className="tc-stats fade-up"
        >
          {bottomStats.map((stat, i) => (
            <div
              key={i}
              style={{
                padding: '1.75rem 1.5rem',
                textAlign: 'center',
                background: i % 2 === 0 ? 'var(--band-cell-a)' : 'var(--band-cell-b)',
                borderRight: i < bottomStats.length - 1 ? '1px solid var(--band-divider)' : 'none',
              }}
            >
              <div style={{
                fontFamily: 'var(--font-heading)', fontWeight: 900,
                fontSize: '2rem', color: 'var(--band-text)', lineHeight: 1,
                marginBottom: '0.4rem',
              }}>
                {stat.value}
              </div>
              <div style={{
                fontFamily: 'var(--font-body)', fontSize: '0.78rem',
                color: 'var(--band-text-45)', letterSpacing: '0.03em',
              }}>
                {stat.label}
              </div>
            </div>
          ))}
        </div>

        {/* ── CTA ────────────────────────────────────────────────────── */}
        <div className="fade-up" style={{ textAlign: 'center', marginTop: '3.5rem' }}>
          <p style={{
            fontFamily: 'var(--font-body)', fontSize: '0.95rem',
            color: 'var(--band-text-50)', marginBottom: '1.5rem',
          }}>
            ¿Tienes un proyecto en mente? Cuéntanos y te preparamos una propuesta.
          </p>
          <button
            onClick={() => document.getElementById('contacto')?.scrollIntoView({ behavior: 'smooth' })}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.6rem',
              background: 'var(--red)', color: 'white',
              fontFamily: 'var(--font-heading)', fontWeight: 700,
              fontSize: '0.875rem', letterSpacing: '0.05em', textTransform: 'uppercase',
              border: 'none', borderRadius: 8, padding: '0.9rem 2rem',
              cursor: 'pointer',
              boxShadow: '0 8px 32px rgba(196,30,58,0.35)',
              transition: 'background 0.3s ease, transform 0.2s ease, box-shadow 0.3s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--red-dark)';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 12px 40px rgba(196,30,58,0.45)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'var(--red)';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 8px 32px rgba(196,30,58,0.35)';
            }}
          >
            Solicita tu propuesta <ArrowRight size={16} />
          </button>
        </div>

      </div>

      <style>{`
        @media (max-width: 1000px) {
          .tc-grid { grid-template-columns: 1fr !important; max-width: 560px; margin-left: auto; margin-right: auto; }
        }
        @media (max-width: 700px) {
          .tc-stats { grid-template-columns: repeat(2, 1fr) !important; }
          .tc-stats > div:nth-child(2) { border-right: none !important; }
          .tc-stats > div:nth-child(1),
          .tc-stats > div:nth-child(2) { border-bottom: 1px solid var(--band-divider); }
        }
      `}</style>
    </section>
  );
};

export default TechnicalCapacity;
